"use client";

import { useFormContext } from "react-hook-form";
import { useTheme } from "next-themes";
import { type CentralIconName } from "@/types/central-icon";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Icon } from "@/components/ui/icon";
import type { OnboardingFormData } from "@/features/onboarding/hooks/use-onboarding-wizard";

const ROLES = ["Recruiter (In-house)", "Recruiter (Agency)", "Hiring Manager", "VC Talent Team", "Founder", "Other"];
const PRIORITIES = ["Finding Talent", "Screening Resumes", "Personal Outreach", "Interviewing Talent", "Assessing Talent Skill", "Other"];
const REFERRAL_SOURCES = ["LinkedIn", "Facebook", "Instagram", "Youtube", "Google", "X (Twitter)", "Perplexity", "ChatGPT", "Word of Mouth", "Friend/Referral", "Other"];

const PLATFORM_ICONS: Record<string, CentralIconName> = {
  LinkedIn: "IconPeopleCircle",
  Facebook: "IconFacebook",
  Instagram: "IconInstagram",
  Youtube: "IconYoutube",
  Google: "IconGoogle",
  "X (Twitter)": "IconX",
  Perplexity: "IconPerplexity",
  ChatGPT: "IconOpenai",
  "Word of Mouth": "IconMegaphone",
  "Friend/Referral": "IconPeople",
};

const PLATFORM_COLORS_DARK: Record<string, { bg: string; border: string; text: string }> = {
  LinkedIn: { bg: "rgba(26,159,255,0.1)", border: "rgba(26,159,255,0.35)", text: "#7bc8ff" },
  Facebook: { bg: "rgba(66,103,178,0.12)", border: "rgba(66,103,178,0.4)", text: "#8b9fdb" },
  Instagram: { bg: "rgba(225,48,108,0.12)", border: "rgba(225,48,108,0.4)", text: "#f0789c" },
  Youtube: { bg: "rgba(255,0,0,0.1)", border: "rgba(255,0,0,0.3)", text: "#ff6b6b" },
  Google: { bg: "rgba(66,133,244,0.1)", border: "rgba(66,133,244,0.35)", text: "#82b1f8" },
  "X (Twitter)": { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.25)", text: "#e0e0e0" },
  Perplexity: { bg: "rgba(32,171,148,0.1)", border: "rgba(32,171,148,0.35)", text: "#5dd4be" },
  ChatGPT: { bg: "rgba(16,163,127,0.1)", border: "rgba(16,163,127,0.35)", text: "#4ddab3" },
  "Word of Mouth": { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.2)", text: "#d0d0d0" },
  "Friend/Referral": { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.2)", text: "#d0d0d0" },
  Other: { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.2)", text: "#d0d0d0" },
};

const PLATFORM_COLORS_LIGHT: Record<string, { bg: string; border: string; text: string }> = {
  LinkedIn: { bg: "rgba(26,130,220,0.1)", border: "rgba(26,130,220,0.3)", text: "#1170b0" },
  Facebook: { bg: "rgba(66,103,178,0.1)", border: "rgba(66,103,178,0.3)", text: "#2d4d8b" },
  Instagram: { bg: "rgba(200,30,90,0.1)", border: "rgba(200,30,90,0.3)", text: "#b0204f" },
  Youtube: { bg: "rgba(200,0,0,0.08)", border: "rgba(200,0,0,0.25)", text: "#b00000" },
  Google: { bg: "rgba(50,110,230,0.1)", border: "rgba(50,110,230,0.3)", text: "#2255bb" },
  "X (Twitter)": { bg: "rgba(0,0,0,0.05)", border: "rgba(0,0,0,0.15)", text: "#333333" },
  Perplexity: { bg: "rgba(20,145,125,0.1)", border: "rgba(20,145,125,0.3)", text: "#0e7a68" },
  ChatGPT: { bg: "rgba(10,145,110,0.1)", border: "rgba(10,145,110,0.3)", text: "#097a5e" },
  "Word of Mouth": { bg: "rgba(0,0,0,0.05)", border: "rgba(0,0,0,0.12)", text: "#555555" },
  "Friend/Referral": { bg: "rgba(0,0,0,0.05)", border: "rgba(0,0,0,0.12)", text: "#555555" },
  Other: { bg: "rgba(0,0,0,0.05)", border: "rgba(0,0,0,0.12)", text: "#555555" },
};

export function StepCustomize() {
  const { control } = useFormContext<OnboardingFormData>();
  const { resolvedTheme } = useTheme();
  const PLATFORM_COLORS = resolvedTheme === "dark" ? PLATFORM_COLORS_DARK : PLATFORM_COLORS_LIGHT;

  return (
    <div className="flex flex-col gap-3.5">
      <p className="text-sm text-contrast/70 leading-5">
        Neuroscale Arbi helps you hire smarter, faster, and fairer—no matter how complex. Build workflows for every stage of the talent lifecycle with ready-to-go templates or from scratch.
      </p>

      <FormField
        control={control}
        name="customize.role"
        render={({ field }) => {
          const value = (field.value as string[]) || [];
          return (
            <FormItem className="flex flex-col gap-1.5">
              <FormLabel required className="font-normal text-foreground leading-5">Which role best describes you?</FormLabel>
              <div className="flex flex-wrap gap-1.5">
                {ROLES.map((r) => (
                  <Pill
                    key={r}
                    label={r}
                    selected={value.includes(r)}
                    onClick={() => {
                      const current = (field.value as string[]) || [];
                      field.onChange(current.includes(r) ? [] : [r]);
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={control}
        name="customize.priorities"
        render={({ field }) => {
          const value = (field.value as string[]) || [];
          return (
            <FormItem className="flex flex-col gap-1.5">
              <FormLabel required className="font-normal text-foreground leading-5">What are your current talent priorities/needs?</FormLabel>
              <div className="flex flex-wrap gap-1.5">
                {PRIORITIES.map((p) => (
                  <Pill
                    key={p}
                    label={p}
                    selected={value.includes(p)}
                    onClick={() => {
                      const current = (field.value as string[]) || [];
                      field.onChange(
                        current.includes(p) ? current.filter((v: string) => v !== p) : [...current, p],
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={control}
        name="customize.referral_source"
        render={({ field }) => {
          const value = (field.value as string[]) || [];
          return (
            <FormItem className="flex flex-col gap-1.5">
              <FormLabel required className="font-normal text-foreground leading-5">How did you hear about Arbi?</FormLabel>
              <div className="flex flex-wrap gap-1.5">
                {REFERRAL_SOURCES.map((s) => (
                  <PlatformPill
                    key={s}
                    label={s}
                    selected={value.includes(s)}
                    onClick={() => {
                      const current = (field.value as string[]) || [];
                      field.onChange(current.includes(s) ? [] : [s]);
                    }}
                    colors={PLATFORM_COLORS[s]}
                    icon={PLATFORM_ICONS[s]}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}

function Pill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant={selected ? "light" : "secondary"}
      size="xs"
      onClick={onClick}
      className={cn(
        "h-auto min-h-0 rounded-full px-2.5 py-1 text-[13px] font-medium shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border overflow-hidden",
        selected && "border-transparent",
        !selected &&
          "border-border dark:border-[rgba(255,255,255,0.15)] bg-contrast/2 dark:bg-[rgba(255,255,255,0.04)] text-foreground/80 dark:text-foreground hover:bg-contrast/4 dark:hover:bg-[rgba(255,255,255,0.06)]",
      )}
    >
      {label}
    </Button>
  );
}

function PlatformPill({ label, selected, onClick, colors, icon }: {
  label: string; selected: boolean; onClick: () => void;
  colors?: { bg: string; border: string; text: string };
  icon?: CentralIconName;
}) {
  const base =
    "flex h-auto min-h-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-medium transition-all cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border overflow-hidden relative";

  const iconEl = icon ? (
    <Icon name={icon} size={14} className="shrink-0 opacity-80" />
  ) : null;

  if (selected && colors) {
    return (
      <Button
        type="button"
        variant="secondary"
        size="xs"
        onClick={onClick}
        className={base}
        style={{ backgroundColor: colors.bg, borderColor: colors.border, color: colors.text }}
      >
        {iconEl}
        {label}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="xs"
      onClick={onClick}
      className={`${base} bg-contrast/2 dark:bg-[rgba(255,255,255,0.04)] text-foreground/80 dark:text-foreground border-border dark:border-[rgba(255,255,255,0.15)]`}
    >
      {iconEl}
      {label}
    </Button>
  );
}
